import { InmateContactResult } from '@reliance/types/models/InmateContact';
import { CdrCategory } from '@reliance/types/enums/CdrCategory';
import type { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import type { OnChangeFn } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Inmate } from '@reliance/types/models/Inmate';
import BaseHtmlTable from '../table/BaseHtmlTable';
import { formatPhoneNumber, toE164 } from '../format/PhoneNumber';
import InmateContactTreatment from './InmateContactTreatment';

type InmateContactTableProps = {
  rows: InmateContactResult[];
  loading: boolean;
  page: number;
  totalPages: number;
  total: number;
  perPage: number;
  serverPagination: PaginationState;
  onServerPaginationChange: OnChangeFn<PaginationState>;
  serverSorting: SortingState;
  onServerSortingChange: OnChangeFn<SortingState>;
  serverColumnFilters: ColumnFiltersState;
  onServerColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  inmate: Inmate;
  onEditContact: (row: InmateContactResult) => void;
};

const InmateContactTable: React.FC<InmateContactTableProps> = (props) => {
  const {
    rows,
    loading,
    page,
    totalPages,
    total,
    perPage,
    serverPagination,
    onServerPaginationChange,
    serverSorting,
    onServerSortingChange,
    serverColumnFilters,
    onServerColumnFiltersChange,
    inmate,
    onEditContact,
  } = props;

  const columnHelper = createColumnHelper<InmateContactResult>();
  const columnDefs = useMemo(
    () => [
      columnHelper.accessor(
        (row) => ({
          ani: row.contact.ani,
          name: row.contact.name,
        }),
        {
          id: 'customer',
          header: 'Customer',
          enableSorting: true,
          meta: { sortName: 'ani' },
          cell: (info) => {
            const { ani, name } = info.getValue();
            return (
              <>
                <Link to={`/account/${toE164(ani)}`}>{formatPhoneNumber(ani)}</Link>
                {name && (
                  <small>
                    <br />
                    Contact Name: {name}
                  </small>
                )}
              </>
            );
          },
        }
      ),
      columnHelper.accessor((row) => row.contact.inmateStatus, {
        id: 'inmateStatus',
        header: 'Inmate Status',
        enableSorting: true,
        meta: { sortName: 'inmateStatus' },
      }),
      columnHelper.accessor((row) => row.contact.customerStatus, {
        id: 'customerStatus',
        header: 'Customer Status',
        enableSorting: true,
        meta: { sortName: 'customerStatus' },
      }),
      columnHelper.accessor((row) => row.contact.unreadCount, {
        id: 'unreadCount',
        header: 'Unread Count',
        enableSorting: true,
        meta: { sortName: 'unreadCount' },
      }),
      columnHelper.accessor(
        (row) => (row.blocked ? 'BLOCKED' : ''),
        {
          id: 'countyBlocked',
          header: 'County Blocked',
          enableSorting: false,
          cell: (info) => info.getValue(),
        }
      ),
      columnHelper.display({
        id: 'texting',
        header: 'Texting',
        cell: ({ row }) => (
          <InmateContactTreatment
            inmate={inmate}
            contact={row.original.contact}
            countyBlocked={!!row.original.blocked}
            treatmentOverride={row.original.contact.textingTreatmentOverride}
            cdrCategory={CdrCategory.TEXT}
          />
        ),
      }),
      columnHelper.display({
        id: 'audioCalling',
        header: 'Audio Calling',
        cell: ({ row }) => (
          <InmateContactTreatment
            inmate={inmate}
            contact={row.original.contact}
            countyBlocked={!!row.original.blocked}
            treatmentOverride={row.original.contact.audioCallTreatmentOverride}
            cdrCategory={CdrCategory.AUDIO_CALL}
          />
        ),
      }),
      columnHelper.display({
        id: 'videoCalling',
        header: 'Video Calling',
        cell: ({ row }) => (
          <InmateContactTreatment
            inmate={inmate}
            contact={row.original.contact}
            countyBlocked={!!row.original.blocked}
            treatmentOverride={row.original.contact.videoCallTreatmentOverride}
            cdrCategory={CdrCategory.VIDEO_CALL}
          />
        ),
      }),
      columnHelper.display({
        id: 'options',
        header: '',
        cell: ({ row }) => (
          <button
            type="button"
            className="btn"
            onClick={() => onEditContact(row.original)}
            aria-label="Edit contact">
            <i className="fas fa-user-edit" />
          </button>
        ),
      }),
    ],
    [columnHelper, inmate, onEditContact]
  );

  return (
    <BaseHtmlTable
      rows={rows}
      columnDefs={columnDefs}
      paginationMode="server"
      page={page}
      totalPages={totalPages}
      total={total}
      perPage={perPage}
      loading={loading}
      serverPagination={serverPagination}
      onServerPaginationChange={onServerPaginationChange}
      serverSorting={serverSorting}
      onServerSortingChange={onServerSortingChange}
      serverColumnFilters={serverColumnFilters}
      onServerColumnFiltersChange={onServerColumnFiltersChange}
    />
  );
};

export default InmateContactTable;
